import {
  ERROR_STATUS_INTERNAL,
  ERROR_STATUS_UNPROCESSABLE,
  TYPE_PHOTO,
} from "shared/constants";
import { MEDIA_SOURCE_PROFILE } from "shared/constants/media";

import { setBanner, setBannerNotFound } from "shared/redux/slices/banners";
import {
  addLikedContent,
  removeLikedContent,
} from "shared/redux/slices/media-gallery";

import { getBanner } from "shared/services/banners";
import {
  deleteMailItem,
  deleteMediaItem,
  getLikedContent,
  getPrivateMedia as purchaseMedia,
  likeContent,
  removeLikeContent,
  uploadChatMediaFile,
  uploadMailMediaFile,
  uploadProfileMediaFile,
} from "shared/services/media";

/**
 * Try to get mail profile
 * @param {Object} media Media file object
 * @param {function} cbSuccess Success callback function
 * @param {function} cbFailed Failed callback function
 * @param {string} source Type from where try upload private image. Possible values [MEDIA_SOURCE_PROFILE, MEDIA_SOURCE_COMMUNICATION]
 * @param {null|number|string} recordUid Id of record. Use it for private mail files
 * @return {(function(*): void)|*}
 */
export const getPrivateMedia = (
  media,
  cbSuccess,
  cbFailed,
  source = MEDIA_SOURCE_PROFILE,
  recordUid = null
) => {
  purchaseMedia(media?.src, source, recordUid)
    .then(({ data }) => {
      if (data?.success && data?.data) {
        cbSuccess(data?.data);
      } else {
        cbFailed({ status: ERROR_STATUS_INTERNAL });
      }
    })
    .catch(({ response }) => {
      cbFailed({ status: response?.status });
    });
};

/**
 * Upload message file to media service
 * @param {string} messageSid Message id
 * @param {Object} file Upload file object
 * @param {function} cbSuccess Success callback function
 * @param {function} cbFailed Failed callback function
 * @param {boolean} isMail Is current request from mail
 */
export const uploadCommunicationFile = (
  messageSid,
  file,
  cbSuccess,
  cbFailed,
  isMail = false
) => {
  const form = new FormData();
  form.append("src", file.file, file.file.name);
  form.append("type", file.type);
  form.append("mailUid", messageSid);

  const method = isMail ? uploadMailMediaFile : uploadChatMediaFile;

  method(form)
    .then(({ data }) => {
      if (!data?.success) {
        cbFailed(file.uid);
        return;
      }
      cbSuccess(file.uid, data.data);
    })
    .catch(() => {
      cbFailed(file.uid);
    });
};

/**
 * Delete attached to mail file
 * @param {number} uid File uid
 * @param {function} cbSuccess Success callback function
 * @param {function} cbFailed Failed callback function
 */
export const deleteMailFile = (uid, cbSuccess, cbFailed) => {
  deleteMailItem({ uid })
    .then(({ data }) => {
      if (!data?.success) {
        cbFailed();
        return;
      }

      cbSuccess();
    })
    .catch(() => {
      cbFailed();
    });
};

/**
 * Remove profile file
 * @param {number} uid File identity
 * @param {boolean} isAvatar Is current file avatar
 * @param {function} cbSuccess Success function callback
 * @param {function} cbFailed Failed function callback
 */
export const deleteProfileFile = (
  uid,
  isAvatar = false,
  cbSuccess,
  cbFailed
) => {
  deleteMediaItem({ uid, isAvatar })
    .then(({ data }) => {
      if (!data?.success) {
        cbFailed(uid);
        return;
      }
      cbSuccess(uid);
    })
    .catch(() => cbFailed(uid));
};

/**
 * Upload profile files to backend
 * @param {file: File, isAvatar: boolean, crop: Object, type: number} file File object
 * @param {boolean} isAvatar Is current file avatar
 * @param {function} cbSuccess Success function callback
 * @param {function} cbFailed Failed function callback
 */
export const uploadProfileFiles = (file, isAvatar, cbSuccess, cbFailed) => {
  const form = new FormData();

  form.append("src", file.file, file.file.name);

  if (file.type === TYPE_PHOTO) {
    form.append(
      "crop",
      JSON.stringify(
        file?.crop?.pix
          ? { ...file?.crop?.pix, ...{ rotate: file?.crop?.rotate ?? 0 } }
          : { ...file?.crop?.crop, ...{ rotate: file?.crop?.rotate ?? 0 } }
      )
    );
    form.append("is_avatar", (+isAvatar).toString());
  }

  form.append("type", file.type.toString());

  uploadProfileMediaFile(form, file.type)
    .then(({ data }) => {
      if (!data?.success || !data?.data) {
        cbFailed(null, file, isAvatar);
        return;
      }

      cbSuccess(data.data, file, isAvatar);
    })
    .catch(({ response }) => {
      if (
        response?.status === ERROR_STATUS_UNPROCESSABLE &&
        response?.data?.errors?.length > 0
      ) {
        cbFailed(response.data.errors, file, isAvatar);
        return;
      }

      cbFailed(null, file, isAvatar);
    });
};

export const handleLikeVideo = (place) => {
  return (dispatch) => {
    getBanner(place)
      .then(({ data }) => {
        if (!data.success) {
          return false;
        }
        dispatch(setBanner(data?.data));
      })
      .catch((error) => {
        console.error(error.toString());
        dispatch(setBannerNotFound(place));
      });
  };
};

export const handleLikeContent = (params) => {
  return (dispatch) => {
    likeContent(params)
      .then(({ data }) => {
        if (!data.success) {
          return false;
        }
        dispatch(addLikedContent(params.uid));
      })
      .catch((error) => {
        console.error(error.toString());
      });
  };
};

export const handleRemoveLikeContent = (params) => {
  return (dispatch) => {
    removeLikeContent(params)
      .then(({ data }) => {
        if (!data.success) {
          return false;
        }
        dispatch(removeLikedContent(params.uid));
      })
      .catch((error) => {
        console.error(error.toString());
      });
  };
};

export const uploadLikedContent = (profileUid, uids) => {
  return (dispatch) => {
    getLikedContent(profileUid, uids)
      .then(({ data }) => {
        if (!data.success) {
          return false;
        }

        data.data.forEach((item) => {
          dispatch(addLikedContent(item.media_uid));
        });
      })
      .catch((error) => {
        console.error(error.toString());
      });
  };
};
